import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Card, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Box, CircularProgress, TextField, InputAdornment, IconButton, Tooltip, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Pagination, Select, MenuItem, FormControl } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import BadgeIcon from '@mui/icons-material/Badge';
import WorkIcon from '@mui/icons-material/Work';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';

import ApiService from '../../../../services/api';
import logger from '../../../../utils/logger';

const Container = styled(Box)(({ theme }) => ({
    padding: '24px',
    backgroundColor: '#f5f7fa',
    minHeight: '100vh',
}));

const Header = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px',
}));

const TitleSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
}));

const TitleIcon = styled(Box)(({ theme }) => ({
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
}));

const SearchAndActions = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
}));

const StyledSearchField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#fff',
        '&:hover fieldset': {
            borderColor: '#3b82f6',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#3b82f6',
        },
    },
}));

const AddButton = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    padding: '10px 20px',
    borderRadius: '10px',
    fontWeight: 600,
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
    '&:hover': {
        background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
        boxShadow: '0 6px 20px rgba(59, 130, 246, 0.5)',
    },
}));

const StatsContainer = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
}));

const StatCard = styled(Card)(({ theme }) => ({
    padding: '20px',
    borderRadius: '16px',
    background: '#fff',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
    },
}));

const StatIconBox = styled(Box)(({ bgcolor }) => ({
    width: '50px',
    height: '50px',
    borderRadius: '12px',
    background: bgcolor || 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
}));

const TableContainerStyled = styled(TableContainer)(({ theme }) => ({
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    overflow: 'hidden',
}));

const Th = styled(TableCell)(({ theme }) => ({
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: '#ffffff',
    fontWeight: 600,
    padding: '16px',
    fontSize: '14px',
    whiteSpace: 'nowrap',
}));

const Td = styled(TableCell)(({ theme }) => ({
    padding: '14px 16px',
    borderBottom: '1px solid #f0f0f0',
    fontSize: '14px',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    transition: 'background-color 0.2s',
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: '#eff6ff',
    },
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
    padding: '8px',
    '&:hover': {
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
    },
}));

const TypeChip = styled(Chip)(({ type }) => {
    const colors = {
        'full-time': { bg: '#dcfce7', color: '#166534' },
        'fulltime': { bg: '#dcfce7', color: '#166534' },
        'part-time': { bg: '#dbeafe', color: '#1e40af' },
        'parttime': { bg: '#dbeafe', color: '#1e40af' },
        'contract': { bg: '#fef3c7', color: '#92400e' },
        'intern': { bg: '#f3e8ff', color: '#7c3aed' },
        'freelance': { bg: '#fce7f3', color: '#be185d' },
        'default': { bg: '#f3f4f6', color: '#4b5563' },
    };
    const normalize = (s) => {
        if (!s) return '';
        return String(s).toLowerCase().replace(/[\s_-]/g, '');
    };
    const typeLower = normalize(type);
    const colorSet = colors[typeLower] || colors.default;
    return {
        backgroundColor: colorSet.bg,
        color: colorSet.color,
        fontWeight: 600,
        fontSize: '12px',
    };
});

const DepartmentChip = styled(Chip)(({ dept }) => {
    const colors = {
        'sales': { bg: '#dbeafe', color: '#1e40af' },
        'marketing': { bg: '#fce7f3', color: '#be185d' },
        'engineering': { bg: '#dcfce7', color: '#166534' },
        'development': { bg: '#dcfce7', color: '#166534' },
        'hr': { bg: '#f3e8ff', color: '#7c3aed' },
        'human resources': { bg: '#f3e8ff', color: '#7c3aed' },
        'finance': { bg: '#fef3c7', color: '#92400e' },
        'operations': { bg: '#e0e7ff', color: '#4338ca' },
        'support': { bg: '#ccfbf1', color: '#0f766e' },
        'default': { bg: '#f3f4f6', color: '#4b5563' },
    };
    const normalize = (s) => {
        if (!s) return '';
        return String(s).toLowerCase();
    };
    const deptLower = normalize(dept);
    const colorSet = colors[deptLower] || colors.default;
    return {
        backgroundColor: colorSet.bg,
        color: colorSet.color,
        fontWeight: 500,
        fontSize: '12px',
    };
});



const EmployeeListTable = ({ onEmployeeDeleted }) => {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [deleteDialog, setDeleteDialog] = useState({ open: false, employee: null });
    const [deleting, setDeleting] = useState(false);

    // Pagination state
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Fetch employees with pagination
    const fetchEmployees = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await ApiService.get(`/admin/employee/get-employees?page=${page}&pageSize=${pageSize}`);
            if (response?.data?.data) {
                setEmployees(response.data.data.employees || []);
                setTotalItems(response.data.data.totalItems || 0);
                setTotalPages(response.data.data.totalPages || 0);
            } else if (response?.data?.employees) {
                setEmployees(response.data.employees || []);
                setTotalItems(response.data.totalItems || 0);
                setTotalPages(response.data.totalPages || 0);
            }
        } catch (err) {
            logger.error('Error fetching employees:', err);
            setError(err.message || 'Failed to fetch employees');
        } finally {
            setLoading(false);
        }
    }, [page, pageSize]);

    useEffect(() => {
        fetchEmployees();
    }, [fetchEmployees]);

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handlePageSizeChange = (event) => {
        setPageSize(event.target.value);
        setPage(1); // Reset to first page when page size changes
    };

    const data = employees || [];

    const handleViewDetails = (employee) => {
        logger.log('View employee details:', employee);
        navigate(`/admin-dashboard/employee-view/${employee.id}`);
    };

    const handleEditEmployee = (employee) => {
        logger.log('Edit employee:', employee);
        navigate(`/admin-dashboard/employee-edit/${employee.id}`);
    };

    const handleDeleteEmployee = (employee) => {
        setDeleteDialog({ open: true, employee });
    };

    const confirmDeleteEmployee = async () => {
        if (!deleteDialog.employee) return;

        setDeleting(true);
        try {
            const response = await ApiService.delete(`/admin/employee/delete-employee/${deleteDialog.employee.id}`);
            logger.log('Employee deleted successfully:', response);

            // Call the callback to refresh the data
            if (onEmployeeDeleted) {
                onEmployeeDeleted(deleteDialog.employee.id);
            }

            // Refresh the employee list
            fetchEmployees();

            setDeleteDialog({ open: false, employee: null });
        } catch (error) {
            logger.error('Error deleting employee:', error);
            // You might want to show a snackbar or alert here
        } finally {
            setDeleting(false);
        }
    };

    const cancelDelete = () => {
        setDeleteDialog({ open: false, employee: null });
    };

    const handleAddEmployee = () => {
        navigate('/admin-dashboard/add-employee');
    };

    const normalized = (v) => (v ?? '').toString().toLowerCase();
    const filtered = useMemo(() => {
        if (!query) return data;
        const q = query.toLowerCase();
        return (data || []).filter((e) => {
            const name = normalized(e.name || e.fullName || [e.first_name, e.last_name].filter(Boolean).join(' '));
            const email = normalized(e.email);
            const dept = normalized(e.department);
            const code = normalized(e.employee_code);
            const type = normalized(e.employee_type);
            const phone = normalized(e.phone_number);
            const designation = normalized(e.designation);
            return name.includes(q) || email.includes(q) || dept.includes(q) || code.includes(q) || type.includes(q) || phone.includes(q) || designation.includes(q);
        });
    }, [data, query]);

    // Calculate stats from current page data
    const departmentCounts = useMemo(() => {
        const counts = {};
        (data || []).forEach(emp => {
            const dept = emp.department || 'Unassigned';
            counts[dept] = (counts[dept] || 0) + 1;
        });
        return counts;
    }, [data]);
    const uniqueDepartments = Object.keys(departmentCounts).length;
    const fullTimeCount = (data || []).filter(e =>
        ['full-time', 'fulltime', 'full time'].includes((e.employee_type || '').toLowerCase().replace(/[\s_-]/g, ''))
    ).length;

    return (
        <Container>
            {/* Header */}
            <Header>
                <TitleSection>
                    <TitleIcon>
                        <PeopleIcon sx={{ fontSize: 26 }} />
                    </TitleIcon>
                    <Box>
                        <Typography variant="h4" fontWeight={700} color="#1f2937">Employees</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage and track your team members
                        </Typography>
                    </Box>
                </TitleSection>
                <SearchAndActions>
                    <StyledSearchField
                        placeholder="Search employees..."
                        size="small"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#3b82f6' }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ width: '280px' }}
                    />
                    <AddButton
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddEmployee}
                    >
                        Add Employee
                    </AddButton>
                </SearchAndActions>
            </Header>

            {/* Error State */}
            {error && (
                <Box sx={{ bgcolor: '#fef2f2', color: '#dc2626', p: 2, borderRadius: '10px', mb: 3 }}>
                    {error}
                </Box>
            )}

            {/* Table */}
            <TableContainerStyled>
                <Table>
                    <TableHead>
                        <TableRow>
                            <Th>#</Th>
                            <Th><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><PeopleIcon fontSize="small" /> Name</Box></Th>
                            <Th><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><EmailIcon fontSize="small" /> Email</Box></Th>
                            <Th><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><WorkIcon fontSize="small" /> Department</Box></Th>
                            <Th><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><BadgeIcon fontSize="small" /> Code</Box></Th>
                            <Th>Type</Th>
                            <Th><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><PhoneIcon fontSize="small" /> Phone</Box></Th>
                            <Th><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><AssignmentIndIcon fontSize="small" /> Designation</Box></Th>
                            <Th align="center">Actions</Th>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <StyledTableRow>
                                <Td colSpan={9} align="center" sx={{ py: 5 }}>
                                    <CircularProgress sx={{ color: '#3b82f6' }} />
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                        Loading employees...
                                    </Typography>
                                </Td>
                            </StyledTableRow>
                        ) : filtered && filtered.length > 0 ? filtered.map((employee, index) => (
                            <StyledTableRow key={employee.id || index} onClick={() => handleViewDetails(employee)}>
                                <Td>
                                    <Typography variant="body2" fontWeight={500} color="#6b7280">
                                        {((page - 1) * pageSize) + index + 1}
                                    </Typography>
                                </Td>
                                <Td>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box
                                            sx={{
                                                width: 36,
                                                height: 36,
                                                borderRadius: '8px',
                                                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: '#fff',
                                                fontWeight: 600,
                                                fontSize: '14px'
                                            }}
                                        >
                                            {(([employee.first_name, employee.last_name].filter(Boolean).join(' ') || employee.name || employee.full_name || employee.fullName || 'N')[0] || 'N').toUpperCase()}
                                        </Box>
                                        <Typography fontWeight={600} color="#1f2937">
                                            {[employee.first_name, employee.last_name].filter(Boolean).join(' ') || employee.name || employee.full_name || employee.fullName || 'N/A'}
                                        </Typography>
                                    </Box>
                                </Td>
                                <Td>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <EmailIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
                                        <Typography variant="body2">{employee.email || 'N/A'}</Typography>
                                    </Box>
                                </Td>
                                <Td>
                                    <DepartmentChip
                                        dept={employee.department}
                                        label={employee.department || 'N/A'}
                                        size="small"
                                    />
                                </Td>
                                <Td>
                                    <Chip
                                        label={employee.employee_code || 'N/A'}
                                        size="small"
                                        sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 500 }}
                                    />
                                </Td>
                                <Td>
                                    <TypeChip
                                        type={employee.employee_type}
                                        label={employee.employee_type || 'N/A'}
                                        size="small"
                                    />
                                </Td>
                                <Td>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <PhoneIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
                                        <Typography variant="body2">{employee.phone_number || 'N/A'}</Typography>
                                    </Box>
                                </Td>
                                <Td>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <AssignmentIndIcon sx={{ fontSize: 16, color: '#3b82f6', opacity: 0.8 }} />
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{employee.designation || '—'}</Typography>
                                    </Box>
                                </Td>
                                <Td align="center">
                                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                                        <Tooltip title="View Details">
                                            <ActionButton
                                                onClick={(e) => { e.stopPropagation(); handleViewDetails(employee); }}
                                                sx={{ color: '#3b82f6' }}
                                            >
                                                <VisibilityIcon fontSize="small" />
                                            </ActionButton>
                                        </Tooltip>
                                        <Tooltip title="Edit Employee">
                                            <ActionButton
                                                onClick={(e) => { e.stopPropagation(); handleEditEmployee(employee); }}
                                                sx={{ color: '#3b82f6' }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </ActionButton>
                                        </Tooltip>
                                        <Tooltip title="Delete Employee">
                                            <ActionButton
                                                onClick={(e) => { e.stopPropagation(); handleDeleteEmployee(employee); }}
                                                sx={{ color: '#ef4444' }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </ActionButton>
                                        </Tooltip>
                                    </Box>
                                </Td>
                            </StyledTableRow>
                        )) : (
                            <StyledTableRow>
                                <Td colSpan={9} align="center" sx={{ py: 5 }}>
                                    <PeopleIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                                    <Typography variant="body1" color="text.secondary">No employees found</Typography>
                                    <Typography variant="body2" color="text.secondary">Try adjusting your search or add a new employee</Typography>
                                </Td>
                            </StyledTableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainerStyled>

            {/* Footer with Pagination */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 3,
                px: 2,
                py: 2,
                backgroundColor: '#fff',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalItems)} of {totalItems} employees
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">Rows per page:</Typography>
                        <FormControl size="small">
                            <Select
                                value={pageSize}
                                onChange={handlePageSizeChange}
                                sx={{
                                    minWidth: 70,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#e5e7eb',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#3b82f6',
                                    },
                                }}
                            >
                                <MenuItem value={5}>5</MenuItem>
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={25}>25</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                                <MenuItem value={100}>100</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    shape="rounded"
                    showFirstButton
                    showLastButton
                    sx={{
                        '& .MuiPaginationItem-root': {
                            fontWeight: 500,
                            '&.Mui-selected': {
                                backgroundColor: '#3b82f6',
                                color: '#fff',
                                '&:hover': {
                                    backgroundColor: '#2563eb',
                                },
                            },
                        },
                    }}
                />
            </Box>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialog.open}
                onClose={cancelDelete}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ color: '#ef4444', fontWeight: 600 }}>
                    Delete Employee
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete <strong>{deleteDialog.employee?.first_name} {deleteDialog.employee?.last_name}</strong>?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={cancelDelete}
                        disabled={deleting}
                        sx={{ color: '#64748b' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmDeleteEmployee}
                        disabled={deleting}
                        variant="contained"
                        color="error"
                        startIcon={deleting ? <CircularProgress size={16} /> : null}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};


export default EmployeeListTable;